import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Heart, Minus, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import { fetchProduct, fetchProducts } from "../lib/api";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { addItem, toggleWishlist, isWishlisted } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setQty(1);
    fetchProduct(slug)
      .then(async (p) => {
        setProduct(p);
        const others = await fetchProducts(p.category).catch(() => []);
        setRelated(others.filter((o) => o.id !== p.id).slice(0, 4));
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="pt-40 text-center text-[#5C5C5C]" data-testid="product-loading">…</div>;
  }
  if (!product) {
    return (
      <div className="pt-40 text-center" data-testid="product-not-found">
        <p className="text-[#5C5C5C]">Not found.</p>
        <Link to="/shop" className="btn-secondary mt-6 inline-flex">Back</Link>
      </div>
    );
  }

  const name = lang === "fr" ? product.name_fr : product.name_en;
  const story = lang === "fr" ? product.story_fr : product.story_en;
  const origin = lang === "fr" ? product.origin_fr : product.origin_en;
  const materials = lang === "fr" ? product.materials_fr : product.materials_en;
  const liked = isWishlisted(product.id);

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) addItem(product);
  };

  return (
    <div className="pt-24 md:pt-28 pb-24" data-testid="page-product">
      <div className="px-6 md:px-12 lg:px-24">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#5C5C5C] hover:text-[#B34A36] mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} />
          {t.product.backToShop}
        </Link>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 relative"
          >
            <div className="aspect-[4/5] overflow-hidden bg-[#F2EFE9] grain">
              <img src={product.image} alt={name} className="w-full h-full object-cover" />
            </div>
          </motion.div>

          <div className="lg:col-span-5 lg:pt-6">
            <p className="overline">{t.categories[product.category]}</p>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light leading-tight mt-3 text-[#1E2B3C]">
              {name}
            </h1>

            <div className="my-8 h-px bg-[#E5E1D8]" />

            <p className="text-[#1A1A1A]/85 leading-relaxed">{story}</p>

            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="overline text-[10px]">{t.product.origin}</dt>
                <dd className="mt-1 text-[#1A1A1A]">{origin}</dd>
              </div>
              <div>
                <dt className="overline text-[10px]">{t.product.artisan}</dt>
                <dd className="mt-1 text-[#1A1A1A]">{product.artisan}</dd>
              </div>
              <div className="col-span-2">
                <dt className="overline text-[10px]">{t.product.materials}</dt>
                <dd className="mt-1 text-[#1A1A1A]">{materials}</dd>
              </div>
            </dl>

            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <div className="flex items-center border border-[#1E2B3C]">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-11 h-12 flex items-center justify-center hover:bg-[#F2EFE9]"
                  data-testid="product-qty-dec"
                  aria-label="Decrease"
                >
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center text-sm" data-testid="product-qty">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="w-11 h-12 flex items-center justify-center hover:bg-[#F2EFE9]"
                  data-testid="product-qty-inc"
                  aria-label="Increase"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                className="btn-primary flex-1 min-w-[200px] justify-center"
                data-testid="product-add-to-cart"
              >
                {t.product.addToCart}
              </button>
              <button
                onClick={() => toggleWishlist(product)}
                className={`w-12 h-12 flex items-center justify-center border ${
                  liked
                    ? "border-[#B34A36] text-[#B34A36]"
                    : "border-[#1E2B3C] text-[#1E2B3C] hover:bg-[#1E2B3C] hover:text-white"
                } transition-colors`}
                data-testid="product-wishlist"
                aria-label="Wishlist"
              >
                <Heart size={16} fill={liked ? "currentColor" : "none"} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-28">
            <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1E2B3C] mb-10">
              {t.product.youMayAlsoLike}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12 md:gap-x-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
